import React from 'react';
import { ImageUploaderProps } from './types';
import { uploadResource } from '../../../api/file.api';
import { LogoutLoader } from '../shared';
import { toast } from 'react-toastify';
import { parseError } from '../../../helpers';
import { SvgTrash } from '../../../assets/svg/general';
import { AIImageGeneratorModal } from '../ai-image-generator';

export const ImageUploader = ({
  name,
  getUpload,
  value,
  onRemove,
  aiEntityType,
  aiEntityId,
  aiDefaultSubject,
}: ImageUploaderProps) => {
  const [fileName, setFileName] = React.useState('');
  const [fileSize, setFileSize] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [aiOpen, setAiOpen] = React.useState(false);
  const inputId = React.useId();

  // "Generate with AI" trigger + modal, shown only when an entity type is provided.
  const aiSection = aiEntityType ? (
    <>
      <button
        type="button"
        onClick={() => setAiOpen(true)}
        className="mt-2 inline-flex items-center gap-1.5 text-sm text-primary medium hover:underline"
      >
        <span aria-hidden>✨</span> Generate with AI
      </button>
      <AIImageGeneratorModal
        active={aiOpen}
        setActive={setAiOpen}
        entityType={aiEntityType}
        entityId={aiEntityId}
        defaultSubject={aiDefaultSubject}
        onAccept={(url) => getUpload(url)}
      />
    </>
  ) : null;

  const handleFileUpload = async (
    file: string | File | Blob | ProgressEvent<FileReader>,
  ) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file as File);
      formData.append('title', name);
      formData.append('description', `Backoffice upload for ${name}`);

      if ((file as File).size > 3 * 1024 * 1024) {
        toast.error('File size must be less than 3MB');
        setLoading(false);
        return;
      }

      setFileSize((file as File).size);
      const res = await uploadResource(formData);
      getUpload(res.url);
    } catch (error) {
      toast.error(parseError(error));
      setFileName('');
      setFileSize(0);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target && e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
      setFileName(e.target.files[0].name);
    }
  };

  const handleRemove = () => {
    setFileName('');
    setFileSize(0);
    onRemove?.();
  };

  const formatSize = (bytes: number) => {
    if (bytes === 0) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const uploadLabel =
    name === 'logoUrl' ? 'logo' : name === 'bannerUrl' ? 'banner' : 'image';

  // Show uploaded file card
  if (value) {
    return (
      <div className="mt-6">
        <div className="border border-mild rounded-xl px-4 py-3 flex items-center gap-3 bg-subtle">
          <img
            src={value}
            alt={uploadLabel}
            className="w-10 h-10 rounded-lg object-cover bg-gray-100 flex-shrink-0"
          />
          <div className="flex-1 min-w-0">
            <p className="text-sm text-text medium truncate">
              {fileName || `${uploadLabel}.png`}
            </p>
            <div className="flex items-center gap-1.5 text-xs text-text-placeholder">
              {fileSize > 0 && <span>{formatSize(fileSize)}</span>}
              {fileSize > 0 && <span>|</span>}
              <svg width={14} height={14} viewBox="0 0 14 14" fill="none">
                <circle cx={7} cy={7} r={6} fill="#14AC14" />
                <path
                  d="M4.5 7L6.25 8.75L9.5 5.25"
                  stroke="white"
                  strokeWidth={1.2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>
          <button
            type="button"
            onClick={handleRemove}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-neutral-500 hover:text-neutral-700 transition-colors flex-shrink-0"
          >
            <SvgTrash className="w-4 h-4" />
          </button>
        </div>
        {aiSection}
      </div>
    );
  }

  // Show upload area
  return (
    <div className="mt-6">
      <label
        htmlFor={inputId}
        className="border border-dashed border-slate-medium rounded-xl p-5 cursor-pointer flex items-center gap-3 bg-subtle hover:bg-white transition-colors"
      >
        {loading ? (
          <div className="w-10 h-10 rounded-lg bg-white border border-slate-medium flex items-center justify-center flex-shrink-0">
            <LogoutLoader />
          </div>
        ) : (
          <div className="w-10 h-10 rounded-lg bg-white border border-slate-medium flex items-center justify-center flex-shrink-0">
            <svg
              width={20}
              height={20}
              viewBox="0 0 20 20"
              fill="none"
              className="text-text-placeholder"
            >
              <path
                d="M3.33301 13.3334L6.99634 9.67002C7.30853 9.35784 7.7295 9.18311 8.16801 9.18311C8.60652 9.18311 9.02749 9.35784 9.33967 9.67002L13.333 13.3334M11.6663 11.6667L13.163 10.17C13.4752 9.85784 13.8962 9.68311 14.3347 9.68311C14.7732 9.68311 15.1942 9.85784 15.5063 10.17L16.6663 11.3334M11.6663 6.66669H11.6747M4.99967 16.6667H14.9997C15.4417 16.6667 15.8656 16.4911 16.1782 16.1785C16.4907 15.866 16.6663 15.4421 16.6663 15V5.00002C16.6663 4.558 16.4907 4.13407 16.1782 3.82151C15.8656 3.50895 15.4417 3.33336 14.9997 3.33336H4.99967C4.55765 3.33336 4.13372 3.50895 3.82116 3.82151C3.5086 4.13407 3.33301 4.558 3.33301 5.00002V15C3.33301 15.4421 3.5086 15.866 3.82116 16.1785C4.13372 16.4911 4.55765 16.6667 4.99967 16.6667Z"
                stroke="currentColor"
                strokeWidth={1.2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        )}
        <div className="text-sm">
          <p>
            <span className="text-primary medium">Upload {uploadLabel}</span>
            <span className="text-text-placeholder"> or drag and drop</span>
          </p>
          <p className="text-text-placeholder text-xs mt-0.5">
            PNG or JPG (max. 800x400px)
          </p>
        </div>
      </label>
      <input
        type="file"
        id={inputId}
        className="hidden"
        onChange={handleChange}
        accept="image/*"
      />
      {aiSection}
    </div>
  );
};
